import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiConfigService } from './api-config.service';
import {
    ApiPaginationService,
    PaginationOptions,
} from '@wdx/shared/infrastructure/api-service';

@Injectable()
export class BaseApiService {
    private apiPaginationService = inject(ApiPaginationService);

    protected get apiBaseUrl(): string {
        return this.apiConfigService.get().apiBaseUrl;
    }

    constructor(
        private http: HttpClient,
        private apiConfigService: ApiConfigService,
    ) {}

    /**
     * Initialtes a get request using the provided apiBaseUrl
     */
    protected _get<Response>(
        path: string,
        options = {},
        paginationOptions?: PaginationOptions,
    ): Observable<Response> {
        return this.http.get<Response>(
            this.buildUrl(path),
            this.buildOptions(options, paginationOptions),
        );
    }

    /**
     * Initialtes a get request relative to the app root
     */
    protected _getRelative<Response>(
        path: string,
        options = {},
        paginationOptions?: PaginationOptions,
    ): Observable<Response> {
        return this.http.get<Response>(
            path,
            this.buildOptions(options, paginationOptions),
        );
    }

    protected _post<Response, Request>(
        path: string,
        body: Request,
        options = {},
        paginationOptions?: PaginationOptions,
    ): Observable<Response> {
        return this.http.post<Response>(
            this.buildUrl(path),
            body,
            this.buildOptions(options, paginationOptions),
        );
    }

    protected _put<Response, Request = any>(
        path: string,
        body: Request,
        options = {},
        paginationOptions?: PaginationOptions,
    ): Observable<Response> {
        return this.http.put<Response>(
            this.buildUrl(path),
            body,
            this.buildOptions(options, paginationOptions),
        );
    }

    protected _patch<Response, Request = any>(
        path: string,
        body: Request,
        options = {},
        paginationOptions?: PaginationOptions,
    ): Observable<Response> {
        return this.http.patch<Response>(
            this.buildUrl(path),
            body,
            this.buildOptions(options, paginationOptions),
        );
    }

    protected _delete<Response>(
        path: string,
        options = {},
        paginationOptions?: PaginationOptions,
    ): Observable<Response> {
        return this.http.delete<Response>(
            this.buildUrl(path),
            this.buildOptions(options, paginationOptions),
        );
    }

    private buildUrl(path: string): string {
        return path.startsWith('/')
            ? `${this.apiBaseUrl}${path}`
            : `${this.apiBaseUrl}/${path}`;
    }

    private buildOptions(
        options: { headers?: any } = {},
        paginationOptions: PaginationOptions = {},
    ) {
        const paginationHeaders = paginationOptions
            ? this.apiPaginationService.mapPaginationOptions(paginationOptions)
            : {};
        const headerContent = {
            ...(options.headers || {}),
            ...paginationHeaders,
        };
        return {
            ...options,
            ...(headerContent ? { headers: headerContent } : {}),
        };
    }
}
